import { cn } from "@marketing-sites/utils/cn";
import Image, { type ImageProps } from "next/image";

import DotDecorators, { type DotDecoratorsVariant } from "../dot-decorators";

export interface TestimonialProps {
  className?: string;
  company: {
    name: string;
    logo?: React.ReactNode;
  };
  quote: React.ReactNode;
  author: {
    name?: string;
    role?: React.ReactNode;
    hideCompanyInRole?: boolean;
  };
  image?: Pick<ImageProps, "src" | "alt" | "width" | "height" | "sizes">;
  dotDecorators?: boolean;
  dotDecoratorVariant?: DotDecoratorsVariant;
  dotDecoratorClassName?: string;
  verticalAlignment?: "top" | "center" | "bottom";
  dotDecoratorsClassName?: string;
  /** "grid" (default) shares one row via a 10-col grid, sized for a full-width section. "stacked" is a top-aligned column for a narrow host like the DRLP hero panel. */
  layout?: "grid" | "stacked";
  /** Omit only for a copy that is not really on the page — a hidden one rendered to measure height. Without it the card is not a tracked region at all, so it fires no visibility event. */
  analyticsId?: string;
}

export default function Testimonial({
  company,
  quote,
  author,
  image,
  className,
  dotDecoratorsClassName,
  dotDecorators = true,
  dotDecoratorVariant = "corners",
  dotDecoratorClassName = dotDecoratorsClassName,
  verticalAlignment = "center",
  layout = "grid",
  analyticsId,
}: TestimonialProps) {
  const openQuote = (
    // Zero-width wrapper hangs the open-quote in the left margin without a magic indent.
    <span aria-hidden="true" className="inline-block w-0">
      <span className="inline-block -translate-x-full pr-[0.15em]">{"“"}</span>
    </span>
  );

  const dotDecoratorsBlock =
    dotDecorators &&
    (dotDecoratorVariant !== "corners" ? (
      <>
        <DotDecorators
          variant="corners"
          className={cn("text-helpdesk xl:hidden", dotDecoratorClassName)}
        />
        <DotDecorators
          variant={dotDecoratorVariant}
          className={cn("text-helpdesk hidden xl:block", dotDecoratorClassName)}
        />
      </>
    ) : (
      <DotDecorators
        variant="corners"
        className={cn("text-helpdesk", dotDecoratorClassName)}
      />
    ));

  const authorCaption = (roleClassName: string, captionClassName?: string) => (
    <figcaption className={captionClassName}>
      <div className="mb-3 w-1/4 md:hidden">
        {image && (
          <Image
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            sizes={image.sizes}
            className="w-full"
          />
        )}
      </div>
      {author.name && (
        <span className="text-body font-sans! block">{author.name}</span>
      )}
      {author.role && (
        <span className={roleClassName}>
          {author.role}
          {!author.hideCompanyInRole && ` at ${company.name}`}
        </span>
      )}
    </figcaption>
  );

  if (layout === "stacked") {
    // Grid, not an absolute headshot: the author row must reserve the portrait's height while figcaption stays a direct child of figure.
    return (
      <figure
        data-analytics-region={analyticsId ? "testimonial" : undefined}
        data-analytics-id={analyticsId}
        className={cn(
          "relative grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1fr)_auto]",
          className,
        )}
      >
        <div className="md:col-span-2">{company?.logo}</div>

        <blockquote className="md:col-span-2">
          <p className="text-callout">
            {openQuote}
            {quote}
            {"”"}
          </p>
        </blockquote>

        {image && (
          <div className="relative hidden h-[174px] w-[139px] self-end overflow-hidden md:col-start-2 md:row-start-3 md:block">
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes="139px"
              className="object-cover"
            />
          </div>
        )}

        {(author.name || author.role) &&
          authorCaption(
            "text-body text-current/60 font-sans! block",
            "self-end md:col-start-1 md:row-start-3",
          )}

        {dotDecoratorsBlock}
      </figure>
    );
  }

  return (
    <figure
      data-analytics-region={analyticsId ? "testimonial" : undefined}
      data-analytics-id={analyticsId}
      className={cn(
        "2xl:aspect-1250/500 relative grid grid-cols-10 gap-x-4 p-6 py-10 md:min-h-[400px] md:p-8",
        className,
      )}
    >
      <div className="col-span-full xl:col-span-2">{company?.logo}</div>

      <div
        className={cn(
          "col-span-full flex flex-col md:col-span-8 xl:col-span-5",
          !image && "md:col-span-10 xl:col-span-8",
          verticalAlignment == "top" && "mb-auto h-full",
          verticalAlignment == "center" && "h-full",
          verticalAlignment == "bottom" && "mt-auto",
        )}
      >
        <blockquote
          className={cn(
            verticalAlignment === "center"
              ? "flex flex-1 items-center py-12 md:py-16"
              : "py-32 pb-12 pt-6 md:py-24",
            verticalAlignment == "top" && "mb-auto",
          )}
        >
          <p className="text-callout max-w-[50ch]">
            {openQuote}
            {quote}
            {"”"}
          </p>
        </blockquote>

        {(author.name || author.role) &&
          authorCaption("text-body text-current/50 font-sans! block")}
      </div>

      {image && (
        <div className="col-span-2 col-start-9 mt-auto hidden md:block">
          <Image
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className="w-full"
          />
        </div>
      )}

      {dotDecoratorsBlock}
    </figure>
  );
}
